import { type ReactNode } from 'react'
import { BigMoveMinContext, bigMoveMin } from './DiffBits'
import VersionViewOverallSection, { type PlanNodeFromServer } from './VersionViewOverallSection'
import VersionViewChannelSection from './VersionViewChannelSection'
import VersionViewQuarterlySection from './VersionViewQuarterlySection'
import { type RevisionBaseline } from './DiffBits'
import { type RowAnchor } from './rowAnchor'
import { type CellAnchor } from './cellAnchor'

interface Channel { id: string; name: string; slug: string; position?: number }
interface Quarter { label: string; start_date: string; end_date: string }
interface MatrixRow { plan_node_id: string; past: Record<string, string>; plan: Record<string, number>; actual?: Record<string, string> }

// Month labels for a revision's actual-vs-plan split; null for a plain version.
type PeriodLabels = {
  actual: string
  actualNumeric: string
  actualQuarters: string
  plan: string
  quarters: string
  past: string
  months: number
} | null

export interface ChannelSectionData { channels: Channel[]; rows: MatrixRow[] }
export interface QuarterlySectionData {
  channels: Channel[]
  past_quarters: Quarter[]
  plan_quarters: Quarter[]
  // Quarters already covered by this version's actual sales. Empty for a
  // plain version.
  actual_quarters?: Quarter[]
  past: Record<string, Record<string, string>>
  plan: Record<string, Record<string, number>>
  actual?: Record<string, Record<string, string>>
  actual_targets?: Record<string, Record<string, number | null>>
  channel_targets: Record<string, number>
}

function SectionHeader({ index, title, first = false }: { index: number; title: string; first?: boolean }) {
  return (
    <div className={`flex items-baseline gap-3 mb-2 ${first ? 'mt-0' : 'mt-5'}`}>
      <span
        className="inline-flex items-center justify-center rounded-full text-xs font-semibold"
        style={{ width: 22, height: 22, background: 'var(--primary)', color: 'var(--primary-foreground, white)' }}
      >
        {index}
      </span>
      <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>{title}</h2>
    </div>
  )
}

// The three read-only breakdown sections shared by the Show page, the
// versioned read-only view and the review page. With `baseline` (a revision
// under review) every cell that differs from the published plan is tinted
// and shows the published value underneath.
export default function PlanBreakdownSections({
  planNodes,
  channelSection,
  quarterlySection,
  currency,
  periodLabels = null,
  baseline = null,
  onRowAction,
  rowExtras,
  onCellAction,
  cellCommentCount,
  cellSelected,
}: {
  planNodes: PlanNodeFromServer[]
  channelSection: ChannelSectionData
  quarterlySection: QuarterlySectionData
  currency: string | null
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  periodLabels?: PeriodLabels
  baseline?: RevisionBaseline | null
  onRowAction?: (anchor: RowAnchor) => void
  rowExtras?: (anchor: RowAnchor) => ReactNode
  onCellAction?: (cell: CellAnchor) => void
  cellCommentCount?: (cell: CellAnchor) => number
  // The cell whose comments are open under its row gets an outline.
  cellSelected?: (cell: CellAnchor) => boolean
}) {
  return (
    <BigMoveMinContext.Provider value={bigMoveMin(currency)}>
      <SectionHeader index={1} title="Overall Plan" first />
      <VersionViewOverallSection
        planNodes={planNodes}
        currency={currency}
        periodLabels={periodLabels}
        baseline={baseline}
        onRowAction={onRowAction}
        rowExtras={rowExtras}
        onCellAction={onCellAction}
        cellCommentCount={cellCommentCount}
        cellSelected={cellSelected}
      />

      <SectionHeader index={2} title="Channel Breakdown" />
      <VersionViewChannelSection
        channels={channelSection.channels}
        rows={channelSection.rows}
        planNodes={planNodes}
        currency={currency}
        periodLabels={periodLabels}
        baseline={baseline}
        onRowAction={onRowAction}
        rowExtras={rowExtras}
        onCellAction={onCellAction}
        cellCommentCount={cellCommentCount}
        cellSelected={cellSelected}
      />

      <SectionHeader index={3} title="Channel Quarterly Breakdown" />
      <VersionViewQuarterlySection
        {...quarterlySection}
        currency={currency}
        baseline={baseline}
        onRowAction={onRowAction}
        rowExtras={rowExtras}
        onCellAction={onCellAction}
        cellCommentCount={cellCommentCount}
        cellSelected={cellSelected}
      />
    </BigMoveMinContext.Provider>
  )
}
